import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ContactsIcon from '@mui/icons-material/Contacts';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import { Gift } from 'lucide-react';
import { toast } from 'react-toastify';
import { tokens } from '../../theme';
import { formatCpfCnpj } from '../../utils';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import Header from '../../components/Header';
import ApprovalSection from './Approvalsection';
import ConsultationValuesSection from './ConsultationValuesSection';

export const Field = ({ label, value }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          textTransform: 'uppercase',
          color: '#9e9e9e',
          mb: '2px',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: value ? 'inherit' : '#bdbdbd',
          fontStyle: value ? 'normal' : 'italic',
        }}
      >
        {value || t('messages.noInformation')}
      </Typography>
    </Box>
  );
};

export const SectionCard = ({ icon, title, subtitle, children }) => (
  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', mb: 2, overflow: 'hidden' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 3,
        py: 1.5,
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fff',
      }}
    >
      {icon}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1F2937' }}>
          {title}
        </Typography>

        {subtitle && (
          <Typography sx={{ fontSize: '12px', color: '#9e9e9e' }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>

    <Box sx={{ px: 3, py: 2.5, backgroundColor: '#fff' }}>{children}</Box>
  </Box>
);

const ResaleDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [simulators, setSimulators] = useState([]);
  const [linkedSimulatorId, setLinkedSimulatorId] = useState('');
  const [savingSimulator, setSavingSimulator] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const { data: response } = await api.get(`/v1/resale/${id}`);
      setData(response);
      setLinkedSimulatorId(response?.company?.simulator_config_id ?? '');
    } catch (error) {
      console.error('Erro ao buscar detalhes da revenda', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSimulators = useCallback(async () => {
    try {
      const { data: res } = await api.get('/v1/simulators');
      setSimulators(res?.data ?? []);
    } catch {
      toast.error('Erro ao carregar simuladores.');
    }
  }, []);

  useEffect(() => {
    fetchDetail();
    fetchSimulators();
  }, [fetchDetail, fetchSimulators]);

  const handleSimulatorChange = async (newSimulatorId) => {
    const previousId = linkedSimulatorId;
    const companyId = data?.company?.id;

    if (!companyId) return;

    setLinkedSimulatorId(newSimulatorId);
    setSavingSimulator(true);

    try {
      if (previousId) {
        await api.delete(`/v1/simulators/${previousId}/resales/${companyId}`);
      }
      if (newSimulatorId) {
        await api.post(`/v1/simulators/${newSimulatorId}/resales/${companyId}`);
      }
      toast.success('Simulador vinculado com sucesso!');
    } catch {
      toast.error('Erro ao atualizar vínculo do simulador.');
      setLinkedSimulatorId(previousId);
    } finally {
      setSavingSimulator(false);
    }
  };

  if (loading) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  const user = data?.user ?? {};
  const company = data?.company ?? {};
  const address = data?.company?.address ?? {};
  const partners = data?.company?.relationships ?? [];

  return (
    <Box m="20px">
      <Button
        onClick={() => navigate(-1)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: colors.grey[300],
          textTransform: 'none',
          padding: 0,
          gap: 1,
          marginBottom: 3,
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <ArrowBackIcon />
        <Typography fontSize={14} fontWeight={400}>
          {t('resale.backToResales')}
        </Typography>
      </Button>

      <Header title={t('resale.resaleDetails')} subtitle={t('resale.approveAndView')} />

      {(company?.access_stage === 'PENDING' || company?.access_stage === 'REJECTED') && (
        <ApprovalSection
          id={id}
          accessStage={company?.access_stage}
          onActionComplete={fetchDetail}
        />
      )}

      <SectionCard
        icon={<BusinessIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('forms.generalData')}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.fullName')} value={user.name} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.email')} value={user.username} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.phone')} value={user.phone} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.companyName')} value={company.name} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.cnpj')} value={formatCpfCnpj(company.taxvar)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.regimeType')} value={company.custom_fields?.tax_regime} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label={t('forms.municipalRegistration')}
              value={company.custom_fields?.municipal_registration}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label={t('forms.stateRegistration')}
              value={company.custom_fields?.state_registration}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        icon={<LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('forms.addressCnpj')}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.place')} value={address.place} />
          </Grid>
          <Grid item xs={4} md={2}>
            <Field label={t('forms.number')} value={address.number} />
          </Grid>
          <Grid item xs={8} md={4}>
            <Field label={t('forms.complement')} value={address.complement} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Field label={t('forms.neighborhood')} value={address.neighborhood} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Field label={t('forms.city')} value={address.city} />
          </Grid>
          <Grid item xs={4} md={3}>
            <Field label={t('forms.state')} value={address.region?.name} />
          </Grid>
          <Grid item xs={4} md={3}>
            <Field label={t('forms.zipCode')} value={address.zipcode} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard icon={<ContactsIcon sx={{ fontSize: 18, color: '#757575' }} />} title="Contatos">
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Field
              label="E-mail Financeiro"
              value={company.custom_fields?.financial_responsible_email}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              label={t('forms.creditEmail')}
              value={company.custom_fields?.credit_release_email}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              label={t('forms.financialEmail')}
              value={company.custom_fields?.financial_responsible_name}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard icon={<PeopleIcon sx={{ fontSize: 18, color: '#757575' }} />} title="Sócios">
        {partners.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: '#bdbdbd', fontStyle: 'italic' }}>
            {t('messages.partnersNotFound')}
          </Typography>
        ) : (
          <Box>
            <Grid container sx={{ mb: 1 }}>
              {[t('forms.name'), t('forms.email'), t('forms.cpf'), t('forms.document')].map((h) => (
                <Grid item xs={3} key={h}>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#9e9e9e',
                    }}
                  >
                    {h}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ mb: 1 }} />
            {partners.map((partner, index) => (
              <Grid
                container
                key={index}
                sx={{
                  py: 1,
                  borderBottom: index < partners.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    {partner.people?.full_name ?? '-'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '13px' }}>{partner.people?.email ?? '-'}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '13px' }}>
                    {formatCpfCnpj(partner.people?.taxvar) ?? '-'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: partner.people?.document ? 'inherit' : '#bdbdbd',
                      fontStyle: partner.people?.document ? 'normal' : 'italic',
                    }}
                  >
                    {partner.people?.document ?? t('messages.noInformation')}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        )}
      </SectionCard>

      <SectionCard
        icon={<Gift size={18} color="#757575" strokeWidth={1.5} />}
        title={t('resale.freeConsults')}
        subtitle={t('resale.freeConsultsSubtitle')}
      >
        <Typography variant="h6" sx={{ fontSize: '12px', color: '#9e9e9e', mb: 1.5 }}>
          {t('resale.freeAvailable').toUpperCase()} <br />{' '}
          <span style={{ color: colors.grey[500], fontWeight: 700, fontSize: '14px' }}>
            {data.granted_consultations || 0}
          </span>
        </Typography>
      </SectionCard>

      <ConsultationValuesSection resaleId={id} />

      <SectionCard
        icon={<SimCardOutlinedIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title="Simulador vinculado"
      >
        <Typography sx={{ fontSize: '12px', color: '#9e9e9e', mb: 1.5 }}>
          Selecione qual simulador estará disponível para esta revenda.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '320px' }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={linkedSimulatorId}
            onChange={(e) => handleSimulatorChange(e.target.value)}
            disabled={savingSimulator}
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="">
              <Typography sx={{ fontSize: '13px', color: '#9e9e9e', fontStyle: 'italic' }}>
                Nenhum
              </Typography>
            </MenuItem>
            {simulators.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
          {savingSimulator && <CircularProgress size={18} />}
        </Box>
      </SectionCard>
    </Box>
  );
};

export default ResaleDetails;
